"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { JsonLd } from "@/components/seo/JsonLd";
import { HelpCircle } from "lucide-react";

export interface FAQItem {
  question: string;
  answer: string;
}

interface CalculatorFAQProps {
  /** FAQ items (4–6 recommended for SEO) */
  items: FAQItem[];
  /** Optional section heading */
  title?: string;
  /** Show icon next to heading (for polished pages) */
  showIcon?: boolean;
  /** Optional className for the wrapper */
  className?: string;
}

export function CalculatorFAQ({
  items,
  title = "Frequently asked questions",
  showIcon,
  className,
}: CalculatorFAQProps) {
  const faqPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <section className={className} aria-labelledby={title ? "faq-heading" : undefined}>
      {title && (
        <h2 id="faq-heading" className="mb-4 flex items-center gap-2 text-xl font-semibold text-foreground">
          {showIcon && (
            <span className="rounded-lg bg-primary/10 p-1.5">
              <HelpCircle className="size-4 text-primary" aria-hidden />
            </span>
          )}
          {title}
        </h2>
      )}
      <JsonLd data={faqPageJsonLd} />
      <Accordion className="w-full">
        {items.map((item, index) => (
          <AccordionItem key={index} value={`faq-${index}`}>
            <AccordionTrigger>{item.question}</AccordionTrigger>
            <AccordionContent>
              <p className="whitespace-pre-line">{item.answer}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
